import React, { useState, useMemo } from 'react';
import { buildRollup, buildAxisCell } from './rollupData';
import { grainOne } from './grainNouns';

// ShapeTab — the "Shape" reading tab. Two parts:
//   1) PUBLIC: the composite scene → sequence → act → whole percentile trajectory
//      (movie.shape_trajectory) — the "shape" the Layered Read case study is about.
//   2) ADMIN-ONLY: four per-level radars of the 12-axis percentile profile
//      (movie.field_standing.matrix), each spoke hover-linked to that axis's rollup.
// field_standing is absent for non-admins (gated in AnalysisController::icicle), so
// the radar block simply doesn't render for them; the trajectory is public.

const AXES = ['contract', 'pressure', 'development', 'movement', 'consequence',
  'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip'];
const AXIS_LABEL = {
  contract: 'Contract', pressure: 'Pressure', development: 'Development', movement: 'Movement',
  consequence: 'Consequence', economy: 'Economy', assembly: 'Assembly', clarity: 'Clarity',
  freshness: 'Freshness', unpredictability: 'Unpredictability', charge: 'Charge', grip: 'Grip',
};
const AXIS_ABBR = {
  contract: 'Con', pressure: 'Pre', development: 'Dev', movement: 'Mov', consequence: 'Cns',
  economy: 'Eco', assembly: 'Asm', clarity: 'Clr', freshness: 'Frs', unpredictability: 'Unp',
  charge: 'Chg', grip: 'Grp',
};
const AXIS_MEANING = {
  contract: 'sets up what it promises and what is at stake',
  pressure: 'builds tension instead of going slack',
  development: 'changes instead of marking time',
  movement: 'moves the story instead of stalling',
  consequence: 'leaves a cost behind instead of resetting',
  economy: 'earns its space instead of wasting it',
  assembly: 'fits together instead of sitting side by side',
  clarity: 'is easy to follow instead of muddy',
  freshness: 'feels new instead of rote',
  unpredictability: 'surprises instead of telegraphing',
  charge: 'carries voltage instead of falling flat',
  grip: 'holds attention instead of letting go',
};
const LENS = {
  contract: 'Design', pressure: 'Design', development: 'Design', movement: 'Design', consequence: 'Design',
  economy: 'Execution', assembly: 'Execution', clarity: 'Execution',
  freshness: 'Involvement', unpredictability: 'Involvement', charge: 'Involvement', grip: 'Involvement',
};
const LENS_COL = { Design: '#3266ad', Execution: '#c98500', Involvement: '#199e70' };
// Order is structure (fine to whole); the nouns are per-format — grainNouns.js. Note these are
// the SINGULAR capitalised forms ('Scene', not 'Scenes'), which is why grainOne() and not
// grainTitle(). 'script' here is the whole-script column and keeps its own word.
const GRAINS = ['scene', 'sequence', 'act', 'script'];
const grainHead = (script, g) => (g === 'script' ? 'Whole' : grainOne(script, g, g));
const NAVY = '#14536e', MUTE = '#8a8880', LINE = '#e1e0d9';

function pct(cell) { return (cell && typeof cell.pct === 'number') ? cell.pct : null; }
function ord(n) {
  const r = n % 10, h = n % 100;
  if (r === 1 && h !== 11) return n + 'st';
  if (r === 2 && h !== 12) return n + 'nd';
  if (r === 3 && h !== 13) return n + 'rd';
  return n + 'th';
}

// ─────────────────────────── Trajectory (public) ───────────────────────────
function Trajectory({ traj, script }) {
  const W = 680, H = 190, padL = 40, padR = 96, padT = 20, padB = 36;
  const pts = GRAINS
    .map((g, i) => { const c = traj[g]; return (c && typeof c.pct === 'number') ? { g, i, pct: c.pct } : null; })
    .filter(Boolean);
  if (pts.length < 2) return null;
  const x = (i) => padL + i * ((W - padL - padR) / 3);
  const y = (p) => padT + (100 - p) * ((H - padT - padB) / 100);
  const line = pts.map((p) => `${x(p.i)},${y(p.pct)}`).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', marginTop: 6 }}>
      <line x1={padL} y1={y(50)} x2={W - padR} y2={y(50)} stroke="#c9ccc4" strokeDasharray="4 4" />
      <text x={W - padR + 6} y={y(50) + 4} fontSize="10" fill="#9a988f">50th</text>
      {[0, 25, 50, 75, 100].map((t) => (
        <text key={t} x={padL - 8} y={y(t) + 3} fontSize="10" fill="#9a988f" textAnchor="end">{t}</text>
      ))}
      <polyline fill="none" stroke={NAVY} strokeWidth="3" points={line} />
      {pts.map((p) => (
        <g key={p.g}>
          <circle cx={x(p.i)} cy={y(p.pct)} r="5.5" fill={NAVY} />
          <text x={x(p.i)} y={y(p.pct) - 11} fontSize="12" fontWeight="700" fill={NAVY} textAnchor="middle">{p.pct}</text>
          <text x={x(p.i)} y={H - 14} fontSize="12" fill="#3f4c54" textAnchor="middle">{grainHead(script, p.g)}</text>
        </g>
      ))}
    </svg>
  );
}

// ───────────────────── One per-level radar (admin), hoverable ─────────────────────
function Radar({ grain, matrix, rollup, cells, onHover }) {
  const S = 150, c = S / 2, R = S / 2 - 26;
  const ptAt = (i, r) => { const a = -Math.PI / 2 + i * 2 * Math.PI / 12; return [c + r * Math.cos(a), c + r * Math.sin(a)]; };
  const vals = AXES.map((ax) => pct(matrix?.[ax]?.[grain]));
  const polyFor = (r) => AXES.map((_, i) => ptAt(i, r).join(',')).join(' ');
  const valPoly = AXES.map((ax, i) => { const v = vals[i]; return v == null ? null : ptAt(i, R * v / 100).join(','); }).filter(Boolean).join(' ');
  return (
    <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} style={{ overflow: 'visible' }}>
      {[25, 50, 75, 100].map((rr) => (
        <polygon key={rr} points={polyFor(R * rr / 100)} fill="none"
          stroke={rr === 50 ? '#b9bdb2' : '#e6e4db'} strokeWidth={rr === 50 ? 1.1 : 0.8}
          strokeDasharray={rr === 50 ? '3 3' : undefined} />
      ))}
      {AXES.map((ax, i) => { const [px, py] = ptAt(i, R); return <line key={ax} x1={c} y1={c} x2={px} y2={py} stroke="#eceae1" />; })}
      {valPoly && <polygon points={valPoly} fill="rgba(20,83,110,.09)" stroke={NAVY} strokeWidth="1.4" />}
      {AXES.map((ax, i) => { const v = vals[i]; if (v == null) return null; const [px, py] = ptAt(i, R * v / 100); return <circle key={ax} cx={px} cy={py} r="3" fill={LENS_COL[LENS[ax]]} />; })}
      {AXES.map((ax, i) => { const [lx, ly] = ptAt(i, R + 12); return <text key={ax} x={lx} y={ly + 3} fontSize="8" fill="#77756c" textAnchor="middle">{AXIS_ABBR[ax]}</text>; })}
      {/* hover hotspots — a circle over each axis's outer spoke/label region (unambiguous at 30° spacing) */}
      {AXES.map((ax, i) => {
        const [hx, hy] = ptAt(i, R + 2);
        return (
          <circle key={'hit' + ax} cx={hx} cy={hy} r="14" fill="transparent" style={{ cursor: 'pointer' }}
            onMouseMove={(e) => onHover({ ax, grain, pctVal: vals[i], rollup, cell: cells?.[ax] }, e)}
            onMouseLeave={() => onHover(null)} />
        );
      })}
    </svg>
  );
}

function TipBody({ tip, script }) {
  const { ax, grain, pctVal, rollup, cell } = tip;
  // Richest first: the miss-pattern for a flagged axis. Otherwise the Tier-2
  // per-axis summary (owed count, strongest/weakest unit, a real move). Otherwise
  // the plain meaning.
  const pat = rollup && (rollup.pattern?.axis === ax ? rollup.pattern : (rollup.secondary?.axis === ax ? rollup.secondary : null));
  return (
    <>
      <span style={{ color: '#ffd98a', fontWeight: 700 }}>{AXIS_LABEL[ax]} · {grainHead(script, grain)}</span>
      {typeof pctVal === 'number' && <> — <span style={{ color: '#9fe0c2', fontWeight: 700 }}>{ord(pctVal)}</span> percentile</>}
      {pat ? (
        <>
          <div style={{ marginTop: 4 }}>{pat.category}</div>
          {pat.move && <span style={{ color: '#cfe4ee', display: 'block', marginTop: 4 }}>Move: {pat.move}</span>}
        </>
      ) : cell && cell.kind === 'ok' ? (
        <>
          <div style={{ marginTop: 4 }}>
            {cell.single
              ? `Reads ${cell.mean}/10 on ${AXIS_LABEL[ax]}.`
              : `Owed in ${cell.owedN} of ${cell.totalN} ${cell.grainPlural} · strongest ${cell.strongest.label}, weakest ${cell.weakest.label}.`}
          </div>
          {cell.suggestion && <span style={{ color: '#cfe4ee', display: 'block', marginTop: 4 }}>Move: {cell.suggestion}</span>}
        </>
      ) : cell && cell.kind === 'not_owed' ? (
        <div style={{ marginTop: 4, color: '#cfe4ee' }}>Rarely called for at the {grain} level.</div>
      ) : (
        <div style={{ marginTop: 4, color: '#cfe4ee' }}>
          Measures whether the {grain} {AXIS_MEANING[ax]}.
        </div>
      )}
    </>
  );
}

// ─────────────────────────────── Shape tab ───────────────────────────────
export default function ShapeTab({ shape, fieldStanding, nodes, articleUrl, script = null }) {
  const [tip, setTip] = useState(null);

  // Deterministic per-level rollups (axis-keyed), so a spoke hover can name that
  // axis's pattern. The engine only writes narrative for scene/sequence/act.
  const rollups = useMemo(() => {
    const out = {};
    ['scene', 'sequence', 'act'].forEach((g) => { try { out[g] = buildRollup(nodes || [], g, script); } catch (e) { out[g] = null; } });
    return out;
  }, [nodes, script]);

  // Tier-2 per-axis summary for every grain × axis (48 cells), so every spoke has a
  // grounded hover line even when the miss-pattern didn't flag it.
  const axisCells = useMemo(() => {
    const out = {};
    GRAINS.forEach((g) => { out[g] = {}; AXES.forEach((ax) => { try { out[g][ax] = buildAxisCell(nodes || [], g, ax, script); } catch (e) { out[g][ax] = null; } }); });
    return out;
  }, [nodes, script]);

  const traj = shape && shape.ok ? shape.trajectory : null;
  const matrix = fieldStanding && fieldStanding.ok ? fieldStanding.matrix : null;
  const refN = (shape && shape.reference_n) || (fieldStanding && fieldStanding.reference_n) || null;
  const medium = (shape && shape.medium) || (fieldStanding && fieldStanding.medium) || 'film';

  const onHover = (payload, e) => {
    if (!payload) { setTip(null); return; }
    const host = e.currentTarget.closest('.shape-radars');
    const rect = host ? host.getBoundingClientRect() : { left: 0, top: 0 };
    setTip({ ...payload, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  if (!traj && !matrix) return null;

  return (
    <div style={{ padding: '2px 0' }}>
      {traj && (
        <div style={{ background: '#fff', border: `0.5px solid ${LINE}`, borderRadius: 12, padding: '14px 16px', margin: '0 0 16px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>The shape of this script</div>
          <p style={{ fontSize: 13, color: '#3f4c54', lineHeight: 1.5, margin: '4px 0 0' }}>
            Where the writing stands against produced {medium}s at each scale — scene, sequence, act, and the whole
            screenplay. A rising line means the parts are good and the whole is better; a falling line means the moments
            outrun the movie.{' '}
            {articleUrl && (
              <a href={articleUrl} style={{ color: NAVY, fontWeight: 600, borderBottom: '1.5px solid #b9c7cf', textDecoration: 'none' }}>Read the case study →</a>
            )}
          </p>
          <Trajectory traj={traj} script={script} />
          <div style={{ fontSize: 12, color: '#55636d', lineHeight: 1.5, marginTop: 8 }}>
            Percentile{refN ? ` vs ${refN} produced ${medium}s` : ''}. 50 = a typical produced film · provisional.
          </div>
        </div>
      )}

      {matrix && (
        <div className="shape-radars" style={{ position: 'relative', background: '#fff', border: `0.5px solid ${LINE}`, borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#8a5a00', background: '#fbe6c8', borderRadius: 999, padding: '2px 8px' }}>Admin · provisional</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>The 12-axis profile at each scale</span>
          </div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', fontSize: 11, color: '#5f5e5a', margin: '2px 0 6px', flexWrap: 'wrap' }}>
            {Object.keys(LENS_COL).map((l) => (
              <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 12, height: 3, borderRadius: 2, background: LENS_COL[l] }} />{l}
              </span>
            ))}
            <span style={{ color: MUTE }}>hover a spoke for its rollup</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {GRAINS.map((g) => {
              const has = AXES.some((ax) => pct(matrix?.[ax]?.[g]) !== null);
              return (
                <div key={g} style={{ border: `0.5px solid ${LINE}`, borderRadius: 10, padding: '8px 4px 6px', background: '#fbfaf6', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: NAVY, fontWeight: 700 }}>{grainHead(script, g)}</div>
                  {has
                    ? <Radar grain={g} matrix={matrix} rollup={rollups[g]} cells={axisCells[g]} onHover={onHover} />
                    : <div style={{ fontSize: 10.5, color: MUTE, padding: '34px 4px' }}>no {g} level</div>}
                </div>
              );
            })}
          </div>
          {tip && (
            <div style={{ position: 'absolute', left: tip.x + 12, top: tip.y + 12, background: '#0f2430', color: '#fff', borderRadius: 8, padding: '9px 11px', fontSize: 11.5, lineHeight: 1.4, maxWidth: 220, boxShadow: '0 8px 22px rgba(0,0,0,.28)', pointerEvents: 'none', zIndex: 20 }}>
              <TipBody tip={tip} script={script} />
            </div>
          )}
          <div style={{ fontSize: 11, color: MUTE, marginTop: 8, lineHeight: 1.5 }}>
            Each spoke is one of the twelve craft axes; distance from center is its percentile at that scale.
            Dashed ring = 50th (a typical produced film).
          </div>
        </div>
      )}
    </div>
  );
}
